import type { ReactNode } from 'react';
import type { TimelineEvent } from '../actions/types';
import type { VirtualAppointmentTeam } from '../../../services/virtualAppointments';
import {
    OPENED_NOW_LABEL,
    formatAnchoredWeeksTrigger,
    formatWeeksTrigger
} from '@odo/shared-ui/clinical/virtualAppointments/summary';
import { formatNogosCount } from '@odo/shared-ui/clinical/virtualAppointments/summary';
import { utcDayOf } from '../../virtual-appointments/virtualAppointmentWeeks';

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function stringField(record: Record<string, unknown>, key: string): string | null {
    const value = record[key];
    return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;
}

function vaLabel(payload: Record<string, unknown>): string | null {
    const type = stringField(payload, 'typeCode') ?? stringField(payload, 'typeCustom');
    const team = stringField(payload, 'team');
    if (type && team) return `${type} · ${team}`;
    return type ?? team;
}

function numberField(record: Record<string, unknown>, key: string): number | null {
    const value = record[key];
    return typeof value === 'number' ? value : null;
}

// "Week 6", or "6 weeks from Oct 1, 2026" when the event carries the VA's own anchor.
function weeksPhrase(payload: Record<string, unknown>, weeks: number): string {
    const anchorDay = utcDayOf(stringField(payload, 'triggerWeeksFrom'));
    return anchorDay === null
        ? `at ${formatWeeksTrigger(weeks)}`
        : `at ${formatAnchoredWeeksTrigger(weeks, anchorDay)}`;
}

// "at Aligner 14" / "at Week 6" / "after 3 no-gos" / "opened now".
function triggerPhrase(payload: Record<string, unknown>): string | null {
    const aligner = numberField(payload, 'triggerAligner');
    const weeks = numberField(payload, 'triggerWeeks');
    const nogos = numberField(payload, 'triggerNogos');
    if (payload.triggerKind === 'aligner' && aligner !== null) return `at Aligner ${aligner}`;
    if (payload.triggerKind === 'weeks' && weeks !== null) return weeksPhrase(payload, weeks);
    if (payload.triggerKind === 'nogos' && nogos !== null) {
        return `after ${formatNogosCount(nogos)}`;
    }
    return payload.triggerKind === 'now' ? OPENED_NOW_LABEL.toLowerCase() : null;
}

function vaWithTriggerBody(event: TimelineEvent, lead: string): ReactNode {
    const payload = isRecord(event.payload) ? event.payload : {};
    const label = vaLabel(payload);
    const phrase = triggerPhrase(payload);
    const when = phrase && `${lead}${phrase}`;
    if (!label && !when) return null;
    return (
        <p className="text-xs text-gray-700 leading-relaxed">
            {[label, when].filter(Boolean).join(', ')}
        </p>
    );
}

// "Vrefi · DM, at Aligner 14" / "Vcomp · DM, after 3 no-gos": the trigger that came due.
export function vaTriggeredBody(event: TimelineEvent): ReactNode {
    return vaWithTriggerBody(event, '');
}

function movedVirtualAppointmentCount(payload: Record<string, unknown>): number {
    const ids = payload.movedVirtualAppointmentIds;
    return Array.isArray(ids) ? ids.filter((id) => typeof id === 'string').length : 0;
}

interface HandoffEventCopy {
    title: string;
    from: VirtualAppointmentTeam;
    to: VirtualAppointmentTeam;
}

const ESCALATION_COPY: HandoffEventCopy = {
    title: 'Escalated to Clinical',
    from: 'DM',
    to: 'Clinical'
};
const HAND_BACK_COPY: HandoffEventCopy = { title: 'Handed back to DM', from: 'Clinical', to: 'DM' };

// Staff handoffs carry a reason; automated transitions and older events don't and keep the default.
function handoffTitle(event: TimelineEvent, copy: HandoffEventCopy): string | null {
    const payload = isRecord(event.payload) ? event.payload : {};
    return stringField(payload, 'reason') ? copy.title : null;
}

function handoffBody(event: TimelineEvent, copy: HandoffEventCopy): ReactNode {
    const payload = isRecord(event.payload) ? event.payload : {};
    const reason = stringField(payload, 'reason');
    if (!reason) return null;
    const moved = movedVirtualAppointmentCount(payload);
    return (
        <div className="text-xs text-gray-700 leading-relaxed space-y-1">
            <p className="italic whitespace-pre-wrap break-words">"{reason}"</p>
            {moved > 0 && (
                <p>
                    Moved {moved} {copy.from} {moved === 1 ? 'VA' : 'VAs'} to {copy.to}
                </p>
            )}
        </div>
    );
}

export function clinicalEscalationTitle(event: TimelineEvent): string | null {
    return handoffTitle(event, ESCALATION_COPY);
}

export function clinicalEscalationBody(event: TimelineEvent): ReactNode {
    return handoffBody(event, ESCALATION_COPY);
}

export function handBackToDmTitle(event: TimelineEvent): string | null {
    return handoffTitle(event, HAND_BACK_COPY);
}

export function handBackToDmBody(event: TimelineEvent): ReactNode {
    return handoffBody(event, HAND_BACK_COPY);
}
